import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import toast from "react-hot-toast";
import { FaEdit } from "react-icons/fa";
import { format } from "date-fns";
import { useAuthContext } from "../../context/auth";
import { useRRHHCalculations } from "../../pages/ER/hooks/useRRHHCalculations";
import { getActivos } from "../../utils/activos";
import { calcularImpuestos } from "../../utils/calcularImpuestos";
import { getFacturasEmitidas, getFacturasRecibidas } from "../../utils/conciliacionSII";
import { FECHA_CORTE, construirLedgerProveedor } from "../../utils/estadoCuenta";
import { getDolarValue, getDolarFallback } from "../../utils/exchangeRateService";
import { getMorasPendientes, getOperacionesFactoring } from "../../utils/factoring";
import { getEdificiosSaldoPendiente } from "../../utils/getEdificiosSaldoPendiente";
import {
  getMovimientosBancarios,
  getCuentasBancarias,
  getFacturasYaVinculadas,
} from "../../utils/movimientosBancarios";
import { getPagosPorMovimientos } from "../../utils/pagosEmpleados";
import { getPipelineCobro } from "../../utils/pipelineCobroSII";
import { getCuotasPagosFuturas, getCuotasCobrosFuturas } from "../../utils/planCobros";
import { getPlanPagosEdificios } from "../../utils/planPagosEdificios";
import { getPlazosCobroParaSII } from "../../utils/plazosCobroSII";
import { procesarVentaDivisas } from "../../utils/procesarVentaDivisas";
import { getProveedoresRecurrentesEntries } from "../../utils/proveedoresRecurrentes";
import { getSaldosIniciales } from "../../utils/saldosIniciales";
import { client } from "../../utils/supabase";
import { useExchangeRates } from "../../utils/useExchangeRates";
import AsignarRecibidaModal from "../ConciliacionSII/AsignarRecibidaModal";
import { Header } from "../Header";
import PlanCuotasModal from "../PlanCuotasModal";
import PlanPagoEdificioModal from "../PlanPagoEdificioModal";
import VincularMovimientoModal from "../VincularMovimientoModal";
import styles from "./FlujoCajaV2.module.scss";

const FILTROS_PROYECCION = [
  { key: "todo", label: "Todo" },
  { key: "cobros", label: "Por cobrar" },
  { key: "pipeline", label: "Pipeline" },
  { key: "proveedores_recurrentes", label: "Recurrentes" },
  { key: "proveedores_facturados", label: "Por pagar" },
  { key: "edificios", label: "Edificios" },
  { key: "rrhh", label: "RRHH" },
  { key: "impuestos", label: "Impuestos" },
  { key: "capex", label: "CapEx" },
  { key: "financiamiento", label: "Créditos" },
];

const FILTROS_HISTORICO = [
  { key: "todo", label: "Todo" },
  { key: "ingresos", label: "Ingresos" },
  { key: "egresos", label: "Egresos" },
];

// Cuatro slots de corte con labels fijos: "Mes" (fin del mes actual), "+1", "+2",
// "Año" (31/12 del año en curso). Las fechas son dinámicas; los labels no.
const calcularFiltrosTemporales = () => {
  const hoy = new Date();
  const yyyy = hoy.getFullYear();
  const m0 = hoy.getMonth();
  const finDeMes = (y, m) => new Date(y, m + 1, 0);
  const toKey = (fecha) => {
    const y = fecha.getFullYear();
    const m = String(fecha.getMonth() + 1).padStart(2, "0");
    const d = String(fecha.getDate()).padStart(2, "0");
    return `corte_${y}-${m}-${d}`;
  };
  const slots = [
    { label: "Mes", fechaCorte: finDeMes(yyyy, m0) },
    { label: "+1", fechaCorte: finDeMes(yyyy, m0 + 1) },
    { label: "+2", fechaCorte: finDeMes(yyyy, m0 + 2) },
    { label: "Año", fechaCorte: new Date(yyyy, 11, 31) },
  ];
  return slots.map((s) => ({ ...s, key: toKey(s.fechaCorte) }));
};

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "-";
  let f = fechaStr;
  if (f.includes("T")) f = f.substring(0, 10);
  const [, month, day] = f.split("-");
  return `${day}/${month}`;
};

// ─── Badge de estado del movimiento bancario ────────────────────────────
// Sistema consistente: cada movimiento muestra un badge según su tipo de vinculación.
// P=pendiente, E=empleado, V=venta (SII emitida), C=compra SII (recibida),
// X=compra extranjera (factura externa, rombo), I=ignorado.
const BADGE_BASE = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: "0.65rem",
  fontWeight: 700,
  width: 18,
  height: 18,
  borderRadius: 4,
  verticalAlign: "middle",
};

// Tooltip estructurado: tipo como encabezado, detalles abajo
const buildTooltip = ({ tipo, folioLabel, folio, entidad }) => (
  <div style={{ textAlign: "left", padding: "2px 4px", minWidth: 140, maxWidth: 260 }}>
    <div
      style={{
        fontSize: "0.65rem",
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        opacity: 0.75,
        marginBottom: 3,
      }}
    >
      {tipo}
    </div>
    {folio && (
      <div style={{ fontSize: "0.78rem", fontWeight: 600, fontFamily: "monospace", marginBottom: entidad ? 2 : 0 }}>
        {folioLabel} {folio}
      </div>
    )}
    {entidad && <div style={{ fontSize: "0.72rem", opacity: 0.9, lineHeight: 1.25 }}>{entidad}</div>}
  </div>
);

const BadgeConTooltip = ({ tooltip, tooltipId, children }) => (
  <OverlayTrigger
    placement="top"
    delay={{ show: 100, hide: 100 }}
    overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}
  >
    {children}
  </OverlayTrigger>
);

const renderBadgeMovimiento = (
  m,
  pagosMovIds,
  enteFiscal = "SII",
  factoringMovIds = new Set(),
  factoringMap = new Map(),
  facturaInfoMap = new Map()
) => {
  // Vinculado a operación de factoring (adelanto o excedente) → F violeta. Prioritario
  // sobre cualquier otro check para que se vea aunque también caiga en algún match secundario.
  if (factoringMovIds.has(m.id)) {
    const entries = factoringMap.get(m.id) || [];
    const provNombre = entries[0]?.op?.proveedor?.nombre || "Factoring";
    const tiposUnicos = [...new Set(entries.map((e) => e.tipoVinc))];
    const tipoLabel = tiposUnicos.length === 1 ? tiposUnicos[0] : "Múltiple";
    const fmtOpts = { style: "currency", currency: m.moneda || "CLP", maximumFractionDigits: 0 };
    const fmtMonto = (n) => Number(n || 0).toLocaleString("es-CL", fmtOpts);

    // 1 op → tooltip clásico vía buildTooltip. N ops → JSX inline con lista por folio.
    let tooltipContent;
    if (entries.length === 1) {
      const e = entries[0];
      const montoVinc = e.tipoVinc === "Giro" ? Number(e.op.giro_a_recibir || 0) : Number(e.op.monto_remanente || 0);
      tooltipContent = buildTooltip({
        tipo: `Factoring · ${e.tipoVinc}`,
        entidad: provNombre,
        folio: montoVinc > 0 ? fmtMonto(montoVinc) : null,
        folioLabel: "Monto",
      });
    } else {
      tooltipContent = (
        <div style={{ textAlign: "left", padding: "2px 4px", minWidth: 180, maxWidth: 280 }}>
          <div
            style={{
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              opacity: 0.75,
              marginBottom: 3,
            }}
          >
            {`Factoring · ${tipoLabel} (${entries.length} ops)`}
          </div>
          <div style={{ fontSize: "0.72rem", opacity: 0.9, lineHeight: 1.25, marginBottom: 4 }}>{provNombre}</div>
          {entries.map((e, i) => {
            const monto = e.tipoVinc === "Giro" ? Number(e.op.giro_a_recibir || 0) : Number(e.op.monto_remanente || 0);
            return (
              <div key={i} style={{ fontSize: "0.72rem", fontFamily: "monospace", lineHeight: 1.3 }}>
                Folio {e.op.factura?.folio || "—"}: {fmtMonto(monto)}
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <BadgeConTooltip tooltip={tooltipContent} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#922b21" }}>F</span>
      </BadgeConTooltip>
    );
  }
  // Pago a empleado → E (chequeo externo, depende del state pagosEmpleadosRegistrados)
  if (pagosMovIds.has(m.id)) {
    return (
      <BadgeConTooltip tooltip={buildTooltip({ tipo: "Pago a empleado" })} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#27ae60" }}>E</span>
      </BadgeConTooltip>
    );
  }
  // Ignorado → I gris (estado, prioridad sobre cualquier categoría)
  if (m.estado === "ignorado") {
    return (
      <BadgeConTooltip tooltip={buildTooltip({ tipo: "Ignorado" })} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#707b7c" }}>I</span>
      </BadgeConTooltip>
    );
  }
  // Resto de badges: derivados de categoria_pago en movimientos_caja (fuente semántica).
  // Esto cubre tanto single (FK directa poblada) como múltiple (FK directa NULL pero
  // movimiento_bancario_facturas tiene N filas) — el badge depende del tipo contable, no del
  // shape de la vinculación.
  // movimientos_caja.movimiento_bancario_id tiene UNIQUE → PostgREST devuelve el embed como objeto.
  const categoriaCaja = m.movimiento_caja?.categoria_pago;

  // Cobro SII (single o múltiple) → V
  if (categoriaCaja === "cobro_factura") {
    // Single: la info (tipo_dte, folio, razon_social_receptor) viene del embed
    // factura_emitida. Multi: factura_emitida queda null al confirmar múltiple →
    // resolver vía facturaInfoMap construido en cliente desde facturas_vinculadas
    // (movimiento_bancario_facturas no tiene FK formal a facturas_sii_emitidas, así
    // que PostgREST no puede embeber el anidado).
    const vincsEmitidas = !m.factura_emitida
      ? (m.facturas_vinculadas || [])
          .filter((v) => v.factura_tipo === "sii_emitida")
          .map((v) => facturaInfoMap.get(v.factura_id))
          .filter(Boolean)
      : [];
    const esCobroExportacion =
      m.factura_emitida?.tipo_dte === 110 || vincsEmitidas.some((info) => info.tipo_dte === 110);
    const foliosMulti = vincsEmitidas.map((i) => i.folio).filter(Boolean);
    const tooltipFolio = m.factura_emitida?.folio ?? (foliosMulti.length > 0 ? foliosMulti.join(", ") : null);
    const tooltipFolioLabel = tooltipFolio ? (foliosMulti.length > 1 ? "Folios" : "Folio") : null;
    const tooltipEntidad = m.factura_emitida?.razon_social_receptor ?? vincsEmitidas[0]?.razon_social_receptor;
    return (
      <BadgeConTooltip
        tooltip={buildTooltip({
          tipo: esCobroExportacion ? "Cobro Exportación" : `Cobro ${enteFiscal}`,
          folioLabel: tooltipFolioLabel,
          folio: tooltipFolio,
          entidad: tooltipEntidad,
        })}
        tooltipId={`tt-${m.id}`}
      >
        <span style={{ ...BADGE_BASE, background: "#2980b9" }}>V</span>
      </BadgeConTooltip>
    );
  }
  // Compra extranjera (factura externa) → X. Cae antes que C porque comparte
  // categoria_pago='pago_proveedor' pero se distingue por match_factura_tipo.
  if (m.match_factura_tipo === "factura_externa") {
    return (
      <BadgeConTooltip
        tooltip={buildTooltip({
          tipo: "Compra extranjera",
          folioLabel: "Doc",
          folio: m.factura_externa?.[0]?.numero_documento,
          entidad: m.match_entidad_nombre,
        })}
        tooltipId={`tt-${m.id}`}
      >
        <span style={{ ...BADGE_BASE, background: "#117a8b" }}>X</span>
      </BadgeConTooltip>
    );
  }
  // Compra SII (single o múltiple) → C
  if (categoriaCaja === "pago_proveedor") {
    return (
      <BadgeConTooltip
        tooltip={buildTooltip({
          tipo: `Compra ${enteFiscal}`,
          folioLabel: m.factura_recibida?.folio ? "Folio" : null,
          folio: m.factura_recibida?.folio,
          entidad: m.factura_recibida?.razon_social_emisor,
        })}
        tooltipId={`tt-${m.id}`}
      >
        <span style={{ ...BADGE_BASE, background: "#7d3c98" }}>C</span>
      </BadgeConTooltip>
    );
  }
  // Pago institucional → I azul oscuro. PREVIRED, SII (declaración), banco, etc.
  if (
    categoriaCaja === "cotizaciones_previsionales" ||
    categoriaCaja === "pago_impuesto" ||
    categoriaCaja === "pago_banco"
  ) {
    return (
      <BadgeConTooltip tooltip={buildTooltip({ tipo: "Pago institucional" })} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#1a3a5c" }}>P</span>
      </BadgeConTooltip>
    );
  }
  // Cambio de moneda (Venta/Compra Divisas) → D azul. categoria_pago='costo_financiero'.
  if (categoriaCaja === "costo_financiero") {
    return (
      <BadgeConTooltip tooltip={buildTooltip({ tipo: "Cambio de Moneda" })} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#1a5276" }}>D</span>
      </BadgeConTooltip>
    );
  }
  // Gasto sin factura → G
  if (categoriaCaja === "gasto_sin_factura") {
    return (
      <BadgeConTooltip tooltip={buildTooltip({ tipo: "Gasto sin factura" })} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#1e8449" }}>G</span>
      </BadgeConTooltip>
    );
  }
  // Pago a cuenta proveedor → A. Movimiento de cuenta corriente sin factura asociada
  // (anticipos, retiros, devoluciones). entidad_tipo='proveedor', cuenta_er=null.
  // Incluye los buckets nuevos pago_factura/pago_credito del modal (mismo badge visual).
  if (categoriaCaja === "pago_a_cuenta" || categoriaCaja === "pago_factura" || categoriaCaja === "pago_credito") {
    return (
      <BadgeConTooltip
        tooltip={buildTooltip({
          tipo: "Pago a cuenta",
          entidad: m.movimiento_caja?.entidad_nombre || m.match_entidad_nombre,
        })}
        tooltipId={`tt-${m.id}`}
      >
        <span style={{ ...BADGE_BASE, background: "#d35400" }}>A</span>
      </BadgeConTooltip>
    );
  }
  // Cobro a cuenta proveedor → A. Ingreso a cuenta corriente del proveedor (devolución,
  // anticipo retornado, refacturación). entidad_tipo='proveedor', cuenta_er=null.
  // Incluye el bucket nuevo cobro_credito del modal (mismo badge visual).
  if (categoriaCaja === "cobro_a_cuenta" || categoriaCaja === "cobro_credito") {
    return (
      <BadgeConTooltip
        tooltip={buildTooltip({
          tipo: "Cobro a cuenta",
          entidad: m.movimiento_caja?.entidad_nombre || m.match_entidad_nombre,
        })}
        tooltipId={`tt-${m.id}`}
      >
        <span style={{ ...BADGE_BASE, background: "#d35400" }}>A</span>
      </BadgeConTooltip>
    );
  }
  // Fallback: tiene mov_caja_id pero categoria_pago no matcheó (raro). Default G.
  if (m.movimiento_caja_id) {
    return (
      <BadgeConTooltip tooltip={buildTooltip({ tipo: "Gasto sin factura" })} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#1e8449" }}>G</span>
      </BadgeConTooltip>
    );
  }
  // Pendiente → guion (la fila se tinta naranja sutil vía isMovPendiente)
  return (
    <BadgeConTooltip tooltip={buildTooltip({ tipo: "Pendiente de vincular" })} tooltipId={`tt-${m.id}`}>
      <span style={{ color: "#999", fontWeight: 400 }}>—</span>
    </BadgeConTooltip>
  );
};

// Detector de pendiente: refleja el fallthrough de renderBadgeMovimiento.
// Se usa para tintar la fila completa, no solo el badge.
const isMovPendiente = (m, pagosMovIds, factoringMovIds = new Set()) => {
  if (factoringMovIds.has(m.id)) return false;
  if (pagosMovIds.has(m.id)) return false;
  if (m.estado === "ignorado") return false;
  const cat = m.movimiento_caja?.categoria_pago;
  if (cat === "cobro_factura") return false;
  if (m.match_factura_tipo === "factura_externa") return false;
  if (cat === "pago_proveedor") return false;
  if (cat === "cotizaciones_previsionales" || cat === "pago_impuesto" || cat === "pago_banco") return false;
  if (cat === "gasto_sin_factura") return false;
  if (cat === "pago_a_cuenta") return false;
  if (cat === "cobro_a_cuenta") return false;
  if (cat === "costo_financiero") return false;
  if (m.movimiento_caja_id) return false;
  return true;
};

const FlujoCajaV2 = () => {
  const { pais, id: userId } = useAuthContext();
  const { loaded: ratesLoaded } = useExchangeRates(pais);
  const monedaLocal = pais === "Chile" ? "CLP" : "PEN";
  const simboloLocal = pais === "Chile" ? "$" : "S/";
  const enteFiscal = pais === "Chile" ? "SII" : "SUNAT";
  const [monedaVista, setMonedaVista] = useState(monedaLocal);
  const [consolidado, setConsolidado] = useState(false);
  const [convertirAUSD, setConvertirAUSD] = useState(false);
  const [filtrosActivos, setFiltrosActivos] = useState(["todo"]);
  // "Año" (31/12) por defecto. Siempre debe haber uno seleccionado — el handler del
  // botón ignora el click si el filtro ya está activo (no permite desactivar).
  const [filtroTemporalKey, setFiltroTemporalKey] = useState(
    () => calcularFiltrosTemporales().find((f) => f.label === "Año")?.key ?? null
  );
  const [filtroHistorico, setFiltroHistorico] = useState("todo");
  const [filtroTiempo, setFiltroTiempo] = useState("ytd");
  const [busquedaHistorico, setBusquedaHistorico] = useState("");
  const [busquedaFuturos, setBusquedaFuturos] = useState("");

  const toggleFiltro = (filtro) => {
    if (filtro === "todo") {
      setFiltrosActivos(["todo"]);
      return;
    }
    setFiltrosActivos((prev) => {
      const sinTodo = prev.filter((f) => f !== "todo");
      if (sinTodo.includes(filtro)) {
        const nuevo = sinTodo.filter((f) => f !== filtro);
        return nuevo.length === 0 ? ["todo"] : nuevo;
      }
      return [...sinTodo, filtro];
    });
  };

  const [movBancarios, setMovBancarios] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [saldosIniciales, setSaldosIniciales] = useState([]);
  const [pendientesCobro, setPendientesCobro] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [proveedoresPendientes, setProveedoresPendientes] = useState([]);
  const [proveedoresRecurrentes, setProveedoresRecurrentes] = useState([]);
  const [impuestosEntries, setImpuestosEntries] = useState([]);
  const [capexEntries, setCapexEntries] = useState([]);
  const [edificiosMap, setEdificiosMap] = useState({});
  const [edificiosSaldo, setEdificiosSaldo] = useState([]);
  const [planPagosEdificios, setPlanPagosEdificios] = useState([]);
  const [pagoEdificioModal, setPagoEdificioModal] = useState(null);
  const [morasPendientes, setMorasPendientes] = useState([]);
  const [operacionesFactoring, setOperacionesFactoring] = useState([]);
  // IDs de movimientos bancarios que ya están vinculados como adelanto o remanente de
  // alguna operación de factoring. Se calcula a partir de operacionesFactoring para no
  // hacer un fetch separado.
  const [movimientosBancarioFactoring, setMovimientosBancarioFactoring] = useState(() => new Set());
  const [mapaFactoring, setMapaFactoring] = useState(() => new Map());
  const [facturaInfoMap, setFacturaInfoMap] = useState(() => new Map());
  const [planCuotasModal, setPlanCuotasModal] = useState(null);
  const [vincularModal, setVincularModal] = useState(null);
  const [modalAsignarRecibida, setModalAsignarRecibida] = useState(null);
  const [cuotasPagosPendientes, setCuotasPagosPendientes] = useState([]);
  const [cuotasCobrosPendientes, setCuotasCobrosPendientes] = useState([]);
  const [yaVinculadasRecibidas, setYaVinculadasRecibidas] = useState(new Set());
  // Saldo neto por proveedor consolidando facturas SII + externas + legacy + movs caja.
  // saldo > 0 = el proveedor tiene un saldo a su favor en sus libros = le debemos
  // (deuda nuestra) = "Por pagar". Sustituye las entries individuales por una consolidada
  // por proveedor para evitar mostrar facturas ya canceladas por pagos sin vincular.
  const [saldosPendientesProveedores, setSaldosPendientesProveedores] = useState([]);
  const [pagosEmpleadosRegistrados, setPagosEmpleadosRegistrados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sincronizandoBanco, setSincronizandoBanco] = useState(false);
  const [sincronizandoSII, setSincronizandoSII] = useState(false);
  const [ultimaConsulta, setUltimaConsulta] = useState(null);

  const currentYear = new Date().getFullYear();
  const { empleadosRRHH, isEmpleadosRRHHLoading, getSueldoBaseVigente } = useRRHHCalculations(
    pais,
    currentYear,
    12,
    [],
    []
  );

  const pagosMovIds = useMemo(
    () => new Set(pagosEmpleadosRegistrados.map((p) => p.movimiento_bancario_id).filter(Boolean)),
    [pagosEmpleadosRegistrados]
  );

  const pagadoLiquidoPorEmpMes = useMemo(() => {
    const map = {};
    for (const p of pagosEmpleadosRegistrados) {
      if (p.tipo !== "sueldo" && p.tipo !== "adelanto") continue;
      if (!p.empleado_id || !p.periodo_año || !p.periodo_mes) continue;
      const key = `${p.empleado_id}-${p.periodo_año}-${p.periodo_mes}`;
      map[key] = (map[key] || 0) + Number(p.monto || 0);
    }
    return map;
  }, [pagosEmpleadosRegistrados]);

  const rrhhEntries = useMemo(() => {
    if (isEmpleadosRRHHLoading || !empleadosRRHH?.length) return [];
    const hoy = new Date().toISOString().split("T")[0];
    const año = new Date().getFullYear();

    const DESCUENTO_EMPLEADO_CL = 0.1827;
    const CARGA_EMPLEADOR_CL = 0.052;
    const DESCUENTO_EMPLEADO_PE = 0.13;
    const CARGA_EMPLEADOR_PE = 0.09;

    const entries = [];

    for (let mesIdx = 0; mesIdx < 12; mesIdx++) {
      const ultimoDiaMes = new Date(año, mesIdx + 1, 0).toISOString().split("T")[0];
      const mesSiguiente = mesIdx === 11 ? 1 : mesIdx + 2;
      const añoSiguiente = mesIdx === 11 ? año + 1 : año;
      const dia12SiguienteMes = `${añoSiguiente}-${String(mesSiguiente).padStart(2, "0")}-12`;

      if (ultimoDiaMes <= hoy && dia12SiguienteMes <= hoy) continue;

      for (const emp of empleadosRRHH) {
        const mesStr = `${año}-${String(mesIdx + 1).padStart(2, "0")}-01`;
        if (emp.egreso_fecha && emp.egreso_fecha < mesStr) continue;

        const bruto = getSueldoBaseVigente(emp, mesIdx) || 0;
        if (bruto <= 0) continue;

        const nombreRaw = `${emp.nombre || ""} ${emp.apellido || ""}`.trim();
        const nombre = nombreRaw
          .toLowerCase()
          .split(" ")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");

        const yaPagado = pagadoLiquidoPorEmpMes[`${emp.id}-${año}-${mesIdx + 1}`] || 0;

        if (pais === "Chile") {
          const liquidoEsperado = Math.round(bruto * (1 - DESCUENTO_EMPLEADO_CL));
          const saldoLiquido = Math.max(0, liquidoEsperado - yaPagado);
          const previred = Math.round(bruto * (DESCUENTO_EMPLEADO_CL + CARGA_EMPLEADOR_CL));

          if (ultimoDiaMes > hoy && saldoLiquido > 0) {
            entries.push({
              id: `rrhh-liquido-${emp.id}-${año}-${mesIdx}`,
              fecha_display: ultimoDiaMes,
              importeEnMoneda: -saldoLiquido,
              razon_social_receptor: `RRHH | ${nombre}${yaPagado > 0 ? " (saldo)" : ""}`,
              tipo: "rrhh",
            });
          }

          if (dia12SiguienteMes > hoy) {
            entries.push({
              id: `rrhh-previred-${emp.id}-${año}-${mesIdx}`,
              fecha_display: dia12SiguienteMes,
              importeEnMoneda: -previred,
              razon_social_receptor: `PREVIRED | ${nombre}`,
              tipo: "rrhh",
            });
          }
        } else {
          const liquidoEsperado = Math.round(bruto * (1 - DESCUENTO_EMPLEADO_PE));
          const saldoLiquido = Math.max(0, liquidoEsperado - yaPagado);
          const essalud = Math.round(bruto * CARGA_EMPLEADOR_PE);

          if (ultimoDiaMes > hoy && saldoLiquido > 0) {
            entries.push({
              id: `rrhh-liquido-${emp.id}-${año}-${mesIdx}`,
              fecha_display: ultimoDiaMes,
              importeEnMoneda: -saldoLiquido,
              razon_social_receptor: `RRHH | ${nombre}${yaPagado > 0 ? " (saldo)" : ""}`,
              tipo: "rrhh",
            });
          }
          if (ultimoDiaMes > hoy) {
            entries.push({
              id: `rrhh-essalud-${emp.id}-${año}-${mesIdx}`,
              fecha_display: dia12SiguienteMes,
              importeEnMoneda: -essalud,
              razon_social_receptor: `EsSalud | ${nombre}`,
              tipo: "rrhh",
            });
          }
        }
      }
    }

    return entries.sort((a, b) => a.fecha_display.localeCompare(b.fecha_display));
  }, [empleadosRRHH, isEmpleadosRRHHLoading, pais, getSueldoBaseVigente, pagadoLiquidoPorEmpMes]);

  const cargarDatosHistoricos = useCallback(async () => {
    if (!pais || pais === "Region" || !ratesLoaded) return;
    const [movs, ctas, saldosInicialesData] = await Promise.all([
      getMovimientosBancarios(pais, { fechaDesde: "2026-01-01" }),
      getCuentasBancarias(pais),
      client.from("saldos_iniciales_cuentas").select("*"),
    ]);

    // Procesar ventas de divisas antes de setear el state
    await procesarVentaDivisas({
      movimientos: movs,
      cuentas: ctas,
      pais,
    });

    // Segundo fetch para traer estados actualizados post-proceso
    const movsActualizados = await getMovimientosBancarios(pais, { fechaDesde: "2026-01-01" });
    const ordenados = [...movsActualizados].sort((a, b) => b.fecha.localeCompare(a.fecha) || b.id - a.id);

    setMovBancarios(ordenados);
    setCuentas(ctas);
    setSaldosIniciales(saldosInicialesData?.data || []);

    // Resolver info (tipo_dte, folio, razon_social_receptor) de facturas emitidas
    // vinculadas vía multi: la tabla puente movimiento_bancario_facturas no tiene FK
    // formal a facturas_sii_emitidas, así que PostgREST no embebe el anidado. Fetch
    // separado y mapa en cliente.
    const idsEmitidasMulti = new Set();
    for (const m of ordenados) {
      for (const v of m.facturas_vinculadas || []) {
        if (v.factura_tipo === "sii_emitida" && v.factura_id != null) {
          idsEmitidasMulti.add(v.factura_id);
        }
      }
    }
    if (idsEmitidasMulti.size > 0) {
      const { data: facsInfo } = await client
        .from("facturas_sii_emitidas")
        .select("id, tipo_dte, folio, razon_social_receptor")
        .in("id", [...idsEmitidasMulti]);
      setFacturaInfoMap(
        new Map(
          (facsInfo || []).map((f) => [
            f.id,
            { tipo_dte: f.tipo_dte, folio: f.folio, razon_social_receptor: f.razon_social_receptor },
          ])
        )
      );
    } else {
      setFacturaInfoMap(new Map());
    }
    try {
      const pagosEmp = await getPagosPorMovimientos(ordenados.map((m) => m.id));
      setPagosEmpleadosRegistrados(pagosEmp);
    } catch {
      setPagosEmpleadosRegistrados([]);
    }
    setUltimaConsulta(new Date());
  }, [pais, ratesLoaded]);

  const cargarDatosFuturos = useCallback(async () => {
    if (!pais || pais === "Region" || !ratesLoaded) return;
    // recibidasPromise se encadena al cómputo de recurrentes (que necesita las facturas SII
    // del mes para suprimir entries sintéticas duplicadas) sin bloquear el resto del Promise.all.
    const recibidasPromise = getFacturasRecibidas(pais, { estado_not: "ignorada" });
    const [
      emitidas,
      pipelineData,
      recibidas,
      recurrentes,
      edificiosData,
      cuotasPagos,
      cuotasCobros,
      yaVinculadas,
      edificiosSaldoData,
      planPagosData,
      morasData,
      operacionesFactoringActivas,
      externasRes,
      legacyRes,
      proveedoresRes,
      movimientosCajaRes,
      saldosInicialesEntidades,
    ] = await Promise.all([
      getFacturasEmitidas(pais, { estado: "vinculada" }),
      getPipelineCobro(pais, monedaLocal),
      recibidasPromise,
      recibidasPromise.then((r) => getProveedoresRecurrentesEntries(pais, monedaLocal, r)),
      client.from("edificios").select("id, edificio").eq("pais", pais),
      getCuotasPagosFuturas(pais),
      getCuotasCobrosFuturas(pais),
      getFacturasYaVinculadas(pais),
      getEdificiosSaldoPendiente({ pais, fechaInicio: "2026-01-01", monedaLocal }),
      getPlanPagosEdificios(pais).catch(() => []),
      getMorasPendientes(pais).catch(() => []),
      // Sin filtro de estado — necesitamos también liquidadas para que mapaFactoring marque
      // con badge F los movs históricos vinculados a ops ya cerradas.
      getOperacionesFactoring(pais).catch(() => []),
      // Fuentes extra de "Por pagar". El dedup contra movs bancarios pagados lo hace
      // yaVinculadas.externas / yaVinculadas.legacy abajo — acá traemos sin pagada.
      client
        .from("facturas_externas")
        .select("id, proveedor_id, proveedor_nombre, fecha_emision, monto_total, moneda, numero_documento, estado")
        .eq("pais", pais)
        .neq("estado", "anulada"),
      client
        .from("facturas_proveedor")
        .select("id, proveedor_id, fecha_emision, fecha_vencimiento, monto_total, moneda, numero_factura, estado")
        .eq("pais", pais)
        .neq("estado", "anulada"),
      client.from("proveedores").select("id, nombre, moneda_default").eq("pais", pais),
      // Movs caja y saldos iniciales: input del ledger para calcular saldo neto por proveedor.
      // Filtro estado != "anulado" + fecha desde FECHA_CORTE replican lo que hace EstadoCuenta.
      client
        .from("movimientos_caja")
        .select(
          "id, fecha, monto, moneda, tipo, entidad_tipo, entidad_id, descripcion, numero_documento, categoria_pago, estado"
        )
        .eq("pais", pais)
        .neq("estado", "anulado")
        .gte("fecha", FECHA_CORTE),
      getSaldosIniciales(pais).catch(() => []),
    ]);
    setPipeline(pipelineData);
    setProveedoresPendientes(recibidas);
    setProveedoresRecurrentes(recurrentes);
    setImpuestosEntries(calcularImpuestos(emitidas, recibidas, pais));
    setEdificiosMap(Object.fromEntries((edificiosData.data || []).map((e) => [e.id, e.edificio])));
    setCuotasPagosPendientes(cuotasPagos || []);
    setCuotasCobrosPendientes(cuotasCobros || []);
    setYaVinculadasRecibidas(yaVinculadas.recibidas || new Set());
    setEdificiosSaldo(edificiosSaldoData || []);
    setPlanPagosEdificios(planPagosData || []);
    setMorasPendientes(morasData || []);
    setOperacionesFactoring(operacionesFactoringActivas || []);
    const idsFactoring = new Set();
    // Map<movId, Array<{op, tipoVinc}>>. Un mismo movimiento bancario puede ser adelanto/excedente
    // de N operaciones (caso típico: Chita agrupa giros de varias facturas en un único depósito).
    // Lee de dos fuentes: las FKs legacy (_adelanto_id / _remanente_id) y la nueva tabla puente
    // operaciones_factoring_movimientos. Se deduplica por (movId, opId) para no contar dos veces
    // cuando una op pueblan ambos.
    const nuevoMapaFactoring = new Map();
    const pushEntry = (key, entry) => {
      idsFactoring.add(key);
      const existing = nuevoMapaFactoring.get(key) || [];
      if (existing.some((e) => e.op.id === entry.op.id && e.tipoVinc === entry.tipoVinc)) return;
      nuevoMapaFactoring.set(key, [...existing, entry]);
    };
    for (const op of operacionesFactoringActivas || []) {
      if (op.movimiento_bancario_adelanto_id) pushEntry(op.movimiento_bancario_adelanto_id, { op, tipoVinc: "Giro" });
      if (op.movimiento_bancario_remanente_id) {
        pushEntry(op.movimiento_bancario_remanente_id, { op, tipoVinc: "Excedente" });
      }
      for (const mv of op.movimientos_vinculados || []) {
        if (!mv.movimiento?.id) continue;
        const tipoVinc = mv.tipo === "adelanto" ? "Giro" : "Excedente";
        pushEntry(mv.movimiento.id, { op, tipoVinc });
      }
    }
    setMovimientosBancarioFactoring(idsFactoring);
    setMapaFactoring(nuevoMapaFactoring);

    // ─── Saldo neto pendiente por proveedor ───
    // Corre construirLedgerProveedor para cada proveedor con actividad en cualquier
    // fuente (SII recibidas + externas + legacy + movs caja). Convención del ledger:
    // saldo > 0 = el proveedor tiene saldo a su favor = le debemos → "Por pagar".
    // Epsilon 0.01 descarta saldos cuasi-cero (facturas + pagos calzando exacto).
    // USD se convierte a moneda local por fecha del item. UF rara en proveedores → identity.
    const recibidasArr = recibidas || [];
    const externasArr = externasRes.data || [];
    const legacyArr = legacyRes.data || [];
    const movsCajaArr = movimientosCajaRes.data || [];
    const saldosInicArr = saldosInicialesEntidades || [];
    const proveedoresArr = proveedoresRes.data || [];
    const provIdsConActividad = new Set();
    for (const f of recibidasArr) if (f.proveedor_id) provIdsConActividad.add(f.proveedor_id);
    for (const f of externasArr) if (f.proveedor_id) provIdsConActividad.add(f.proveedor_id);
    for (const f of legacyArr) if (f.proveedor_id) provIdsConActividad.add(f.proveedor_id);
    for (const m of movsCajaArr) {
      if (m.entidad_tipo === "proveedor" && m.entidad_id) provIdsConActividad.add(m.entidad_id);
    }
    const nombreProvMap = Object.fromEntries(proveedoresArr.map((p) => [p.id, p.nombre]));
    const tdcLocal = (monto, monedaOrigen, fecha) => {
      if (!monto) return 0;
      const origen = monedaOrigen || monedaLocal;
      if (origen === monedaLocal) return monto;
      if (origen === "USD") {
        const rate = getDolarValue(fecha, pais) || getDolarFallback(pais) || 1;
        return Math.round(monto * rate);
      }
      return monto;
    };
    // tdcUSD: simétrico — convierte local → USD dividiendo por dólar del día. Para
    // proveedores predominantemente USD genera una segunda entry en USD (sin convertir).
    const tdcUSD = (monto, monedaOrigen, fecha) => {
      if (!monto) return 0;
      const origen = monedaOrigen || monedaLocal;
      if (origen === "USD") return monto;
      if (origen === monedaLocal) {
        const rate = getDolarValue(fecha, pais) || getDolarFallback(pais) || 1;
        return rate > 0 ? Math.round(monto / rate) : 0;
      }
      return monto;
    };
    const saldosProvs = [];
    for (const provId of provIdsConActividad) {
      const saldoInicial = saldosInicArr.find((s) => s.entidad_tipo === "proveedor" && s.entidad_id === provId) || null;
      const ledgerArgs = {
        provId,
        siiRecibidas: recibidasArr,
        facturasExternas: externasArr,
        facturasProveedor: legacyArr,
        movimientosCaja: movsCajaArr,
        saldoInicial,
        monedaPrincipal: monedaLocal,
      };
      const nombre = nombreProvMap[provId] || `Proveedor #${provId}`;
      // Determinar moneda del proveedor desde moneda_default. Fallback: monedaLocal.
      const provObj = proveedoresArr.find((p) => p.id === provId);
      const provMoneda = provObj?.moneda_default || monedaLocal;
      const esProvUSD = provMoneda === "USD";
      const tdcProv = esProvUSD ? tdcUSD : tdcLocal;

      const ledger = construirLedgerProveedor({ ...ledgerArgs, toDisplayCurrency: tdcProv });
      if (ledger.length === 0) continue;
      const saldo = ledger[0].saldo;
      if (saldo > 0.01) {
        saldosProvs.push({ entidadId: provId, nombre, saldo, moneda: provMoneda });
      }
    }
    setSaldosPendientesProveedores(saldosProvs);

    const capex = await getActivos(pais, { es_planificado: true });
    setCapexEntries(capex);
    const plazosMap = await getPlazosCobroParaSII(emitidas);
    const emitidasConPlazos = emitidas.map((f) => {
      const key = `${f.venta_tipo}-${f.venta_ref_id}-${f.venta_ref_index ?? ""}`;
      const plazos = plazosMap.get(key) || { plazocobro: null, facturafechacobro: null };
      return {
        ...f,
        plazocobro: plazos.plazocobro ?? f.plazocobro,
        facturafechacobro: plazos.facturafechacobro ?? f.facturafechacobro,
        moneda_dolar_origen: plazos.moneda_dolar_origen ?? false,
      };
    });
    setPendientesCobro(emitidasConPlazos);
  }, [pais, monedaLocal, ratesLoaded]);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      await cargarDatosHistoricos();
      await cargarDatosFuturos();
    } catch {
      toast.error("Error cargando datos");
    } finally {
      setLoading(false);
    }
  }, [cargarDatosHistoricos, cargarDatosFuturos]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Actualización optimista al confirmar vinculación: el modal devuelve el objeto del
  // movimiento bancario actualizado (con campos cambiados + embed de movimiento_caja)
  // y acá reemplazamos solo esa fila en el state local. Sin recargar nada más.
  // Excepción: empleado pasa flag _empleado=true porque el badge E depende de
  // pagosEmpleadosRegistrados (state separado) — ese caso sí requiere cargarDatos.
  // Flujos de factoring: el payload trae _factoringOp y _factoringTipo (vincular) o
  // _desvinculaFactoring (desvincular). Mantienen sincronizados mapaFactoring y el Set
  // movimientosBancarioFactoring para que el badge F aparezca/desaparezca sin reload.
  const handleVincularSuccess = useCallback(
    (movActualizado) => {
      if (!movActualizado?.id) {
        cargarDatos();
        return;
      }

      if (movActualizado._empleado) {
        cargarDatos();
        return;
      }

      setMovBancarios((prev) =>
        prev.map((m) => {
          if (m.id !== movActualizado.id) return m;
          const merged = { ...m, ...movActualizado };
          delete merged._reload;
          delete merged._empleado;
          delete merged._factoringOp;
          delete merged._factoringTipo;
          delete merged._desvinculaFactoring;
          return merged;
        })
      );

      if (movActualizado._factoringOp && movActualizado._factoringTipo) {
        setMapaFactoring((prev) => {
          const next = new Map(prev);
          const existing = next.get(movActualizado.id) || [];
          next.set(movActualizado.id, [
            ...existing,
            { op: movActualizado._factoringOp, tipoVinc: movActualizado._factoringTipo },
          ]);
          return next;
        });
        setMovimientosBancarioFactoring((prev) => {
          const next = new Set(prev);
          next.add(movActualizado.id);
          return next;
        });
      } else if (movActualizado._desvinculaFactoring) {
        setMapaFactoring((prev) => {
          const next = new Map(prev);
          next.delete(movActualizado.id);
          return next;
        });
        setMovimientosBancarioFactoring((prev) => {
          const next = new Set(prev);
          next.delete(movActualizado.id);
          return next;
        });
      }
    },
    [cargarDatos]
  );

  const handleSyncBanco = useCallback(async () => {
    setSincronizandoBanco(true);
    try {
      const hoy = new Date();
      const desde = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-01`;
      const hasta = hoy.toISOString().split("T")[0];
      const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/clay-import?pais=${pais}&desde=${desde}&hasta=${hasta}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      const resultados = data.resultados || [];
      const totalNuevos = resultados.reduce((s, r) => s + (r.nuevos || 0), 0);
      const totalClay = resultados.reduce((s, r) => s + (r.clay_total || 0), 0);
      const erroresReales = resultados.filter((r) => r.error && !r.error.includes("Sin movimientos"));

      if (totalNuevos > 0) {
        toast.success(
          `Banco: ${totalNuevos} movimiento${totalNuevos === 1 ? "" : "s"} nuevo${totalNuevos === 1 ? "" : "s"}`
        );
      } else {
        toast(
          `Banco sin novedades. Clay devolvió ${totalClay} movimiento${totalClay === 1 ? "" : "s"} desde ${desde}, todos ya estaban. Si falta alguno reciente, Clay todavía no lo sincronizó con el banco.`,
          { duration: 7000 }
        );
      }
      if (erroresReales.length > 0) {
        toast.error(
          `${erroresReales.length} cuenta${erroresReales.length === 1 ? "" : "s"} con error de sincronización`
        );
      }
      await cargarDatos();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error sincronizando banco:", err);
      toast.error("Error al actualizar banco");
    } finally {
      setSincronizandoBanco(false);
    }
  }, [pais, cargarDatos]);

  const handleSyncSII = useCallback(async () => {
    setSincronizandoSII(true);
    try {
      const hoy = new Date();
      const periodo = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}`;
      const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/sii-sync?periodo=${periodo}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      const nuevasEm = data.emitidas?.nuevas || 0;
      const nuevasRe = data.recibidas?.nuevas || 0;
      const totalNuevas = nuevasEm + nuevasRe;
      if (totalNuevas > 0) {
        toast.success(
          `${enteFiscal} (${periodo}): ${nuevasEm} emitida${nuevasEm === 1 ? "" : "s"} + ${nuevasRe} recibida${nuevasRe === 1 ? "" : "s"} nuevas`
        );
      } else {
        toast(
          `${enteFiscal} sin novedades para ${periodo}. ${data.clay_total || 0} DTEs revisados, todos ya estaban.`,
          {
            duration: 6000,
          }
        );
      }
      await cargarDatos();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error sincronizando SII:", err);
      toast.error(`Error al actualizar ${enteFiscal}`);
    } finally {
      setSincronizandoSII(false);
    }
  }, [cargarDatos, enteFiscal]);

  // Moneda en la que se muestra todo. Independiente de qué cuentas se incluyen
  // (eso lo controla `consolidado`). `convertirAUSD` solo aplica cuando estás en
  // la pestaña local — en USD ya está en dólares.
  const monedaDisplay = monedaVista === "USD" || convertirAUSD ? "USD" : monedaLocal;

  // Convierte un monto entre USD ↔ local usando la cotización de la fecha provista.
  // Para fechas futuras, getDolarValue cae en fallback a la fecha anterior más cercana
  // (la última cotización conocida), que es exactamente lo que queremos para proyecciones.
  const convertir = useCallback(
    (monto, monedaOrigen, monedaDestino, fecha) => {
      const n = Number(monto || 0);
      if (!n || monedaOrigen === monedaDestino) return n;
      const tasa = getDolarValue(fecha, pais) || getDolarFallback(pais) || 1;
      if (monedaOrigen === "USD") return Math.round(n * tasa);
      return Math.round(n / tasa);
    },
    [pais]
  );

  // Saldo derivado de una ancla histórica conocida (saldos_iniciales_cuentas) más la
  // suma de todos los movimientos posteriores. No depende de cuentas_bancarias.saldo_actual,
  // que tiene desfase entre el endpoint de saldo y el de movimientos en Clay.
  const calcularSaldoConAncla = useCallback(
    (moneda) => {
      const ancla = saldosIniciales.find((s) => s.moneda === moneda);
      if (!ancla) return 0;
      const ajuste = movBancarios.reduce((sum, m) => {
        if (m.cuenta_bancaria_id !== ancla.cuenta_bancaria_id) return sum;
        if (!m.fecha || m.fecha <= ancla.fecha) return sum;
        const monto = Number(m.monto || 0);
        return sum + (m.tipo === "ingreso" ? monto : -monto);
      }, 0);
      return Number(ancla.monto) + ajuste;
    },
    [saldosIniciales, movBancarios]
  );

  const saldoActualLocal = useMemo(() => calcularSaldoConAncla(monedaLocal), [calcularSaldoConAncla, monedaLocal]);

  const saldoActualUSD = useMemo(() => calcularSaldoConAncla("USD"), [calcularSaldoConAncla]);

  // Saldo expresado en monedaDisplay. Si consolidado, suma ambas cuentas (la otra
  // se convierte con la cotización de hoy → fallback a la última conocida).
  const hoyStr = new Date().toISOString().split("T")[0];
  const saldoActual = useMemo(() => {
    const localEnDisplay = convertir(saldoActualLocal, monedaLocal, monedaDisplay, hoyStr);
    const usdEnDisplay = convertir(saldoActualUSD, "USD", monedaDisplay, hoyStr);
    if (consolidado) return localEnDisplay + usdEnDisplay;
    return monedaVista === "USD" ? usdEnDisplay : localEnDisplay;
  }, [saldoActualLocal, saldoActualUSD, monedaLocal, monedaDisplay, consolidado, monedaVista, convertir, hoyStr]);

  const rangosFecha = useMemo(() => {
    const hoy = new Date();
    const hoyStr = hoy.toISOString().split("T")[0];
    const diaSemana = hoy.getDay() === 0 ? 7 : hoy.getDay();
    const lunes = new Date(hoy);
    lunes.setDate(hoy.getDate() - diaSemana + 1);
    const lunesStr = lunes.toISOString().split("T")[0];
    return {
      hoy: { desde: hoyStr, hasta: hoyStr },
      esta_semana: { desde: lunesStr, hasta: hoyStr },
      este_mes: { desde: `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-01`, hasta: hoyStr },
      ytd: { desde: `${hoy.getFullYear()}-01-01`, hasta: hoyStr },
    };
  }, []);

  // Lista de meses para el dropdown: desde el mes más antiguo con movimientos cargados
  // (o enero 2026 como fallback) hasta el mes anterior al actual. El mes en curso tiene
  // su propio botón "Este mes". El límite es dinámico para que escale conforme se cargan
  // datos históricos más antiguos.
  const mesesDropdown = useMemo(() => {
    const opciones = [];
    const hoy = new Date();
    const fechaMasAntigua =
      movBancarios.length > 0
        ? movBancarios.reduce((min, m) => (m.fecha && m.fecha < min ? m.fecha : min), movBancarios[0].fecha)
        : "2026-01-01";
    const [yLim, mLim] = fechaMasAntigua.split("-").map(Number);
    const limite = new Date(yLim, mLim - 1, 1);

    let cursor = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
    while (cursor >= limite) {
      const y = cursor.getFullYear();
      const m = cursor.getMonth();
      const mm = String(m + 1).padStart(2, "0");
      const labelMes = cursor.toLocaleDateString("es", { month: "long" }).replace(/^\w/, (c) => c.toUpperCase());
      opciones.push({
        key: `mes_${y}-${mm}`,
        label: `${labelMes} ${y}`,
        desde: `${y}-${mm}-01`,
        hasta: new Date(y, m + 1, 0).toISOString().split("T")[0],
      });
      cursor = new Date(y, m - 1, 1);
    }
    return opciones;
  }, [movBancarios]);

  const movimientosFiltrados = useMemo(() => {
    // Mapa cuentaId → moneda de la cuenta. Si consolidado, incluyo todas las cuentas;
    // si no, solo las de la moneda activa.
    const cuentaMoneda = Object.fromEntries(cuentas.map((c) => [c.id, c.moneda]));
    const cuentaIdsValidos = consolidado
      ? new Set(cuentas.map((c) => c.id))
      : new Set(cuentas.filter((c) => c.moneda === monedaVista).map((c) => c.id));
    let lista = movBancarios.filter((m) => cuentaIdsValidos.has(m.cuenta_bancaria_id));
    if (filtroHistorico === "ingresos") lista = lista.filter((m) => m.tipo === "ingreso");
    if (filtroHistorico === "egresos") lista = lista.filter((m) => m.tipo === "egreso");
    const rango =
      filtroTiempo === "hoy"
        ? rangosFecha.hoy
        : filtroTiempo === "esta_semana"
          ? rangosFecha.esta_semana
          : filtroTiempo === "este_mes"
            ? rangosFecha.este_mes
            : filtroTiempo === "ytd"
              ? rangosFecha.ytd
              : mesesDropdown.find((m) => m.key === filtroTiempo);
    if (rango) lista = lista.filter((m) => m.fecha >= rango.desde && m.fecha <= rango.hasta);
    if (busquedaHistorico.trim()) {
      const q = busquedaHistorico.trim().toLowerCase();
      lista = lista.filter((m) => (m.descripcion_raw || "").toLowerCase().includes(q));
    }
    // Enriquecemos primero (monto convertido + moneda origen). El cálculo del saldo
    // depende de si hay algún filtro activo.
    const enriched = lista.map((m) => {
      const monedaOrigen = cuentaMoneda[m.cuenta_bancaria_id] || monedaLocal;
      const montoEnDisplay = convertir(Number(m.monto || 0), monedaOrigen, monedaDisplay, m.fecha);
      return { ...m, monto_display: montoEnDisplay, moneda_origen: monedaOrigen };
    });

    const hayFiltroActivo = busquedaHistorico.trim() !== "" || filtroHistorico !== "todo" || filtroTiempo !== "ytd";

    if (hayFiltroActivo) {
      // Con filtro activo el saldo "real" de cada fila sería inconsistente: la lista
      // oculta movs intermedios, así que caminar hacia atrás desde saldoActual da
      // valores que no coinciden con cartola. En su lugar mostramos el acumulado
      // running de la selección, partiendo de 0 en el más antiguo (sirve como
      // "¿cuánto suman los movs visibles hasta acá?"). La lista entra en orden DESC,
      // calculamos en ASC y devolvemos en DESC para no alterar el render.
      const asc = [...enriched].reverse();
      let acc = 0;
      const ascConSaldo = asc.map((m) => {
        acc += m.tipo === "ingreso" ? m.monto_display : -m.monto_display;
        return { ...m, saldo_calculado: acc };
      });
      return ascConSaldo.reverse();
    }

    // Sin filtro activo: saldo "real" — partir del saldo de ahora y deshacer hacia atrás.
    let saldo = saldoActual;
    return enriched.map((m) => {
      const entry = { ...m, saldo_calculado: saldo };
      if (m.tipo === "ingreso") saldo -= m.monto_display;
      else saldo += m.monto_display;
      return entry;
    });
  }, [
    movBancarios,
    cuentas,
    monedaVista,
    monedaLocal,
    monedaDisplay,
    consolidado,
    saldoActual,
    convertir,
    filtroHistorico,
    filtroTiempo,
    rangosFecha,
    mesesDropdown,
    busquedaHistorico,
  ]);

  const proyeccionesFuturas = useMemo(() => {
    const hoy = new Date().toISOString().split("T")[0];
    // Filtro temporal: si está activo, calcular la fecha de corte como string YYYY-MM-DD
    // para comparar lex contra entry.fecha_display (también YYYY-MM-DD).
    const fechaCorte = filtroTemporalKey
      ? calcularFiltrosTemporales().find((f) => f.key === filtroTemporalKey)?.fechaCorte
      : null;
    const fechaCorteStr = fechaCorte
      ? `${fechaCorte.getFullYear()}-${String(fechaCorte.getMonth() + 1).padStart(2, "0")}-${String(fechaCorte.getDate()).padStart(2, "0")}`
      : null;
    // Entries sin fecha (fecha_display null) quedan fuera cuando hay corte activo —
    // no podemos colocarlos antes ni después con certeza.
    const dentroDeCorte = (e) => !fechaCorteStr || (!!e.fecha_display && e.fecha_display <= fechaCorteStr);
    const filtrarPorCorte = (arr) => (fechaCorteStr ? arr.filter(dentroDeCorte) : arr);
    const mostrarTodo = filtrosActivos.includes("todo");
    const mostrarFacturado = mostrarTodo || filtrosActivos.includes("cobros");
    const mostrarPipeline = mostrarTodo || filtrosActivos.includes("pipeline");
    const mostrarProveedoresRecurrentes = mostrarTodo || filtrosActivos.includes("proveedores_recurrentes");
    const mostrarProveedoresFact = mostrarTodo || filtrosActivos.includes("proveedores_facturados");
    const mostrarProveedores = mostrarProveedoresRecurrentes || mostrarProveedoresFact;
    const mostrarRRHH = mostrarTodo || filtrosActivos.includes("rrhh");
    const mostrarImpuestos = mostrarTodo || filtrosActivos.includes("impuestos");
    const esUSD = monedaVista === "USD";
    // Cuándo incluir items USD vs local. Si consolidado, ambos. Si no, solo los de la moneda activa.
    const incluirUSD = consolidado || esUSD;
    const incluirLocal = consolidado || !esUSD;

    // Convierte un importe en moneda origen a la moneda de display, usando la fecha del item.
    const aDisplay = (monto, monedaOrig, fecha) => convertir(monto, monedaOrig, monedaDisplay, fecha);

    const mapFacturado = (f) => {
      // monto_total siempre está en moneda local (SII reporta en CLP/PEN, también
      // para facturas con origen USD). moneda_origen es la moneda "natural" de la
      // operación desde la perspectiva del usuario y se usa para badge/filtro.
      const monedaOrig = f.moneda_dolar_origen ? "USD" : monedaLocal;
      const importeEnMoneda = aDisplay(Number(f.monto_total || 0), monedaLocal, f.fecha_emision);
      return {
        ...f,
        fecha_display: f.plazocobro || null,
        importeEnMoneda,
        moneda_origen: monedaOrig,
        tipo: "facturado",
        folio: f.folio,
      };
    };
    const mapPipeline = (p) => {
      // pipeline trae importe (USD si moneda_dolar=true) y importeCLP (local).
      const monedaOrig = p.moneda_dolar ? "USD" : monedaLocal;
      const importeOrig = p.moneda_dolar ? p.importe : (p.importeCLP ?? p.importe);
      return {
        ...p,
        fecha_display: p.fecha_estimada,
        importeEnMoneda: aDisplay(Number(importeOrig || 0), monedaOrig, p.fecha_estimada),
        moneda_origen: monedaOrig,
        razon_social_receptor: p.cliente,
        tipo: "pipeline",
      };
    };
    const facturadoFiltrado = pendientesCobro
      .filter((f) => !f.facturafechacobro && (!f.plazocobro || f.plazocobro > hoy))
      .filter((f) => f.estado_factoring !== "cedida")
      .filter((f) => (f.moneda_dolar_origen === true ? incluirUSD : incluirLocal));
    // Para cada factura emitida, verificar si tiene cuotas de cobro
    const facturadoEntries = [];
    if (mostrarFacturado) {
      for (const f of facturadoFiltrado) {
        // monto_cuota está en la misma moneda que monto_total (CLP/PEN). monedaOrig
        // refleja la moneda "natural" de la operación para badge/filtro.
        const monedaOrig = f.moneda_dolar_origen ? "USD" : monedaLocal;
        const cuotasFactura = cuotasCobrosPendientes.filter((c) => c.factura_sii_emitida_id === f.id);
        if (cuotasFactura.length > 0) {
          cuotasFactura.forEach((c) => {
            facturadoEntries.push({
              ...f,
              fecha_display: c.fecha_vencimiento,
              importeEnMoneda: aDisplay(Number(c.monto_cuota), monedaLocal, c.fecha_vencimiento),
              moneda_origen: monedaOrig,
              razon_social_receptor: f.razon_social_receptor,
              tipo: "facturado",
              folio: f.folio,
              cuota_actual: c.numero_cuota,
              total_cuotas: cuotasFactura.length,
            });
          });
        } else {
          facturadoEntries.push(mapFacturado(f));
        }
      }
    }

    // Facturas cedidas al factoring — dos entries por operación: giro a recibir
    // (anticipo neto de costos en fecha_anticipo) y excedente (en fecha_cobro_final).
    // Cada entry desaparece cuando el movimiento bancario correspondiente queda vinculado
    // (movimiento_bancario_adelanto_id / movimiento_bancario_remanente_id no nulos).
    const factoringEntries = [];
    if (mostrarFacturado) {
      // Las proyecciones futuras solo se generan para ops activas. Liquidadas / anuladas
      // viven en el state para que el panel histórico pueda marcar sus movs con badge F.
      for (const op of operacionesFactoring.filter((o) => o.estado === "activa")) {
        const proveedorFactoring = op.proveedor?.nombre || "Factoring";
        const cliente = op.factura?.razon_social_receptor || "";
        const tieneAdelantoVinc =
          !!op.movimiento_bancario_adelanto_id ||
          (op.movimientos_vinculados || []).some((mv) => mv.tipo === "adelanto");
        const tieneRemanenteVinc =
          !!op.movimiento_bancario_remanente_id ||
          (op.movimientos_vinculados || []).some((mv) => mv.tipo === "remanente");
        if (op.giro_a_recibir > 0 && !tieneAdelantoVinc) {
          const fechaGiro = op.fecha_anticipo || op.fecha_operacion;
          factoringEntries.push({
            id: `factoring-giro-${op.id}`,
            factura_sii_emitida_id: op.factura_sii_emitida_id,
            fecha_display: fechaGiro,
            importeEnMoneda: aDisplay(Number(op.giro_a_recibir), monedaLocal, fechaGiro),
            moneda_origen: monedaLocal,
            razon_social_receptor: `Fact. ${proveedorFactoring} — ${cliente}`,
            tipo: "facturado",
            folio: op.factura?.folio || null,
            es_factoring: true,
          });
        }
        const remanente = op.monto_remanente ?? Number(op.monto_factura || 0) - Number(op.capital_adelantado || 0);
        if (remanente > 0 && !tieneRemanenteVinc) {
          factoringEntries.push({
            id: `factoring-excedente-${op.id}`,
            factura_sii_emitida_id: op.factura_sii_emitida_id,
            fecha_display: op.fecha_cobro_final,
            importeEnMoneda: aDisplay(remanente, monedaLocal, op.fecha_cobro_final),
            moneda_origen: monedaLocal,
            razon_social_receptor: `Fact. ${proveedorFactoring} — Excedente — ${cliente}`,
            tipo: "facturado",
            folio: op.factura?.folio || null,
            es_factoring: true,
          });
        }
      }
    }

    const pipe = mostrarPipeline ? pipeline.map(mapPipeline).filter((p) => p.importeEnMoneda > 0) : [];

    // "Por pagar" — saldo neto consolidado por proveedor + fallback a facturas SII
    // individuales para los proveedores SIN saldo entry. El saldo cubre SII recibidas +
    // externas + legacy + pagos desde FECHA_CORTE, así que las 3 fuentes individuales
    // se reemplazan por UNA entry consolidada por proveedor cuando hay actividad.
    // Comparación SIEMPRE por proveedor_id numérico (regla 10.7).
    const provIdsConSaldoPendiente = new Set(saldosPendientesProveedores.map((s) => s.entidadId));

    const provsEntries = [];

    // Entries de saldo neto por proveedor. El saldo se computa en monedaLocal en
    // cargarDatosFuturos; el filtro USD/local respeta s.moneda (acá siempre local).
    for (const s of saldosPendientesProveedores) {
      const esUSDSaldo = s.moneda === "USD";
      if (esUSDSaldo ? !incluirUSD : !incluirLocal) continue;
      provsEntries.push({
        id: `saldo-proveedor-${s.entidadId}`,
        fecha_display: hoy,
        importeEnMoneda: -aDisplay(s.saldo, s.moneda || monedaLocal, hoy),
        moneda_origen: s.moneda || monedaLocal,
        razon_social_receptor: s.nombre,
        tipo: "proveedor",
        es_saldo_pendiente: true,
      });
    }

    // Fallback: facturas SII recibidas de proveedores SIN saldo entry. Cubre el caso
    // raro de una factura pendiente cuyo saldo neto es ≤ 0.01 (pagos previos cubrieron
    // la deuda) — la factura sigue siendo informativa para el panel.
    const provFilter = (f) => !yaVinculadasRecibidas.has(f.id) && !provIdsConSaldoPendiente.has(f.proveedor_id);
    const provsBase = incluirLocal ? proveedoresPendientes.filter(provFilter) : [];
    for (const f of provsBase) {
      const cuotasFactura = cuotasPagosPendientes.filter((c) => c.factura_sii_recibida_id === f.id);
      if (cuotasFactura.length > 0) {
        cuotasFactura.forEach((c) => {
          provsEntries.push({
            ...f,
            fecha_display: c.fecha_vencimiento,
            importeEnMoneda: -aDisplay(Number(c.monto_cuota), monedaLocal, c.fecha_vencimiento),
            moneda_origen: monedaLocal,
            razon_social_receptor: f.razon_social_emisor,
            tipo: "proveedor",
            folio: f.folio,
            cuota_actual: c.numero_cuota,
            total_cuotas: cuotasFactura.length,
          });
        });
      } else {
        provsEntries.push({
          ...f,
          fecha_display: f.plazopago || null,
          importeEnMoneda: -aDisplay(Number(f.monto_total || 0), monedaLocal, f.plazopago || f.fecha_emision),
          moneda_origen: monedaLocal,
          razon_social_receptor: f.razon_social_emisor,
          tipo: "proveedor",
          folio: f.folio,
          vencida: f.plazopago && f.plazopago < hoy,
        });
      }
    }

    // Recurrentes: pueden ser USD o moneda local. importe ya está en su moneda.
    const provsRecurrentesFiltrados = proveedoresRecurrentes
      .filter((p) => (p.moneda_recurrente === "USD" ? incluirUSD : incluirLocal))
      .map((p) => ({
        ...p,
        importeEnMoneda: -aDisplay(
          Math.abs(Number(p.importeEnMoneda || 0)),
          p.moneda_recurrente || monedaLocal,
          p.fecha_display
        ),
        moneda_origen: p.moneda_recurrente === "USD" ? "USD" : monedaLocal,
      }));
    const provsFiltrados = mostrarProveedores
      ? [
          ...(mostrarProveedoresFact ? provsEntries : []),
          ...(mostrarProveedoresRecurrentes ? provsRecurrentesFiltrados : []),
        ]
      : [];

    // RRHH e impuestos siempre se calculan en moneda local. Si no incluyo local, no van.
    const reexpresarLocal = (e) => ({
      ...e,
      importeEnMoneda: -aDisplay(Math.abs(Number(e.importeEnMoneda || 0)), monedaLocal, e.fecha_display),
      moneda_origen: monedaLocal,
    });
    const rrhh = mostrarRRHH && incluirLocal ? rrhhEntries.map(reexpresarLocal) : [];
    const impuestos = mostrarImpuestos && incluirLocal ? impuestosEntries.map(reexpresarLocal) : [];

    const mostrarCapex = mostrarTodo || filtrosActivos.includes("capex");
    const mapCapex = (a) => {
      const monedaOrig = a.moneda === "USD" ? "USD" : monedaLocal;
      return {
        id: `capex-${a.id}`,
        fecha_display: a.fecha_planificada,
        importeEnMoneda: -aDisplay(Number(a.costo_unitario), monedaOrig, a.fecha_planificada),
        moneda_origen: monedaOrig,
        razon_social_receptor: [
          a.destino_id ? edificiosMap[a.destino_id] || `Edificio #${a.destino_id}` : null,
          a.tipo_activo?.toUpperCase(),
          a.proveedor?.nombre || null,
        ]
          .filter(Boolean)
          .join(" | "),
        tipo: "capex",
      };
    };
    const capexFiltrado = capexEntries
      .filter((a) => a.fecha_planificada && a.fecha_planificada > hoy)
      .filter((a) => (a.moneda === "USD" ? incluirUSD : incluirLocal));
    const capex = mostrarCapex ? capexFiltrado.map(mapCapex) : [];

    // Edificios: si hay pagos planificados en plan_pagos_edificios, se genera una entry
    // por cada plan + (opcional) una fila residual con el saldo no planificado en la
    // próxima fecha trimestral. Si no hay planes, una sola entry con el saldo total.
    // Vienen siempre en moneda local.
    const mostrarEdificios = mostrarTodo || filtrosActivos.includes("edificios");
    const edificiosEntries = [];
    if (mostrarEdificios && incluirLocal) {
      for (const e of edificiosSaldo) {
        const plansEdificio = planPagosEdificios.filter((p) => p.edificio_id === e.edificioId);
        if (plansEdificio.length > 0) {
          for (const plan of plansEdificio) {
            edificiosEntries.push({
              id: `edificio-plan-${plan.id}`,
              fecha_display: plan.fecha_acordada,
              importeEnMoneda: -Number(plan.monto || 0),
              moneda_origen: e.moneda,
              razon_social_receptor: e.edificioNombre,
              tipo: "edificio",
              folio: plan.nota || null,
              edificioData: e,
              planId: plan.id,
            });
          }
          const totalPlanificado = plansEdificio.reduce((sum, p) => sum + Number(p.monto || 0), 0);
          const saldoRestante = Math.round(Number(e.saldoPendiente || 0) - totalPlanificado);
          if (saldoRestante > 0) {
            edificiosEntries.push({
              id: `edificio-residual-${e.edificioId}`,
              fecha_display: e.proximaFecha,
              importeEnMoneda: -saldoRestante,
              moneda_origen: e.moneda,
              razon_social_receptor: e.edificioNombre,
              tipo: "edificio",
              folio: null,
              edificioData: e,
            });
          }
        } else {
          edificiosEntries.push({
            id: `edificio-${e.edificioId}`,
            fecha_display: e.proximaFecha,
            importeEnMoneda: -e.saldoPendiente,
            moneda_origen: e.moneda,
            razon_social_receptor: e.edificioNombre,
            tipo: "edificio",
            folio: null,
            edificioData: e,
          });
        }
      }
    }

    // Créditos: moras pendientes de operaciones de factoring. Siempre en moneda local.
    const mostrarCreditos = mostrarTodo || filtrosActivos.includes("financiamiento");
    const mapMora = (m) => ({
      id: `mora-${m.id}`,
      fecha_display: m.fecha_notificacion,
      importeEnMoneda: -Number(m.monto_mora || 0),
      moneda_origen: monedaLocal,
      razon_social_receptor: [
        "Mora factoring",
        m.operacion?.factura?.folio ? `Folio ${m.operacion.factura.folio}` : null,
        m.operacion?.factura?.razon_social_receptor || null,
      ]
        .filter(Boolean)
        .join(" — "),
      tipo: "credito",
      folio: null,
    });
    const creditosEntries = mostrarCreditos && incluirLocal ? morasPendientes.map(mapMora) : [];

    let todos = [
      ...filtrarPorCorte(facturadoEntries),
      ...filtrarPorCorte(factoringEntries),
      ...filtrarPorCorte(pipe),
      ...filtrarPorCorte(provsFiltrados),
      ...filtrarPorCorte(rrhh),
      ...filtrarPorCorte(impuestos),
      ...filtrarPorCorte(capex),
      ...filtrarPorCorte(edificiosEntries),
      ...filtrarPorCorte(creditosEntries),
    ].sort((a, b) => (a.fecha_display || "9999").localeCompare(b.fecha_display || "9999"));

    // Búsqueda libre: nombre (razon_social_receptor), folio o monto absoluto redondeado.
    // Aplica DESPUÉS de filtrosActivos/corte temporal, antes del cálculo de saldo —
    // ojo que el saldo acumulado refleja sólo lo visible (mismo trade-off que el histórico).
    if (busquedaFuturos.trim()) {
      const q = busquedaFuturos.trim().toLowerCase();
      todos = todos.filter(
        (m) =>
          (m.razon_social_receptor || "").toLowerCase().includes(q) ||
          String(m.folio || "")
            .toLowerCase()
            .includes(q) ||
          String(Math.abs(Math.round(Number(m.importeEnMoneda) || 0))).includes(q)
      );
    }

    // Saldo proyectado parte del saldo actual (ya en monedaDisplay).
    let saldo = saldoActual;
    return todos.map((f) => {
      saldo += f.importeEnMoneda;
      return { ...f, saldo_proyectado: saldo };
    });
  }, [
    pendientesCobro,
    pipeline,
    proveedoresPendientes,
    proveedoresRecurrentes,
    saldosPendientesProveedores,
    yaVinculadasRecibidas,
    cuotasPagosPendientes,
    cuotasCobrosPendientes,
    rrhhEntries,
    impuestosEntries,
    capexEntries,
    edificiosMap,
    edificiosSaldo,
    planPagosEdificios,
    morasPendientes,
    operacionesFactoring,
    filtrosActivos,
    filtroTemporalKey,
    monedaVista,
    monedaLocal,
    monedaDisplay,
    consolidado,
    saldoActual,
    convertir,
    busquedaFuturos,
  ]);

  const fmt = (n) =>
    monedaDisplay === "USD"
      ? `US$ ${Number(n).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`
      : `${simboloLocal} ${Number(n).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;

  // Badge pill con el código corto de la moneda origen. Azul claro para USD, naranja/beige
  // para local. Se usa en la columna "Ref" cuando consolidado está activo.
  const renderBadgeMoneda = (monedaOrigen) => {
    if (!monedaOrigen) return null;
    const esUSD = monedaOrigen === "USD";
    const txt = esUSD ? "US$" : monedaLocal;
    return (
      <span
        style={{
          display: "inline-block",
          padding: "1px 6px",
          fontSize: "0.65rem",
          fontWeight: 700,
          letterSpacing: "0.04em",
          borderRadius: 3,
          background: esUSD ? "#e8f4f8" : "#fef5e7",
          color: esUSD ? "#2980b9" : "#d35400",
          border: `1px solid ${esUSD ? "#aed6f1" : "#fdebd0"}`,
          lineHeight: "14px",
        }}
      >
        {txt}
      </span>
    );
  };

  // Borde lateral izquierdo en filas cuya moneda origen difiere de la de display.
  // Sólo aplica en consolidado (cuando hay mezcla de orígenes posible).
  const bordeMonedaStyle = (monedaOrigen) => {
    if (!consolidado || !monedaOrigen || monedaOrigen === monedaDisplay) return undefined;
    const color = monedaOrigen === "USD" ? "#3498db" : "#e67e22";
    return { boxShadow: `inset 3px 0 0 ${color}` };
  };

  // Saldo al final de la proyección (última fila ordenada ASC) — se muestra
  // siempre en el banner verde. Fallback a saldoActual cuando no hay proyecciones.
  const saldoFinalProyectado = useMemo(
    () => proyeccionesFuturas.at(-1)?.saldo_proyectado ?? saldoActual,
    [proyeccionesFuturas, saldoActual]
  );

  const countPorPagarPendientes = useMemo(
    () => proveedoresPendientes.filter((f) => f.estado_vinculacion === "pendiente").length,
    [proveedoresPendientes]
  );

  const gridRef = useRef(null);

  const movPendientes = movBancarios.filter(
    (m) =>
      !m.factura_sii_emitida_id &&
      !m.factura_sii_recibida_id &&
      !m.movimiento_caja_id &&
      !pagosMovIds.has(m.id) &&
      m.estado !== "ignorado"
  );
  const pendientesVincular = movPendientes.length;
  const pendientesPorMoneda = movPendientes.reduce((acc, m) => {
    const mon = m.moneda || monedaLocal;
    acc[mon] = (acc[mon] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className={styles.wrapper}>
      <Header
        leftContent={
          <div style={{ display: "flex", alignItems: "center", gap: 32, marginLeft: 150 }}>
            <div
              style={{
                display: "flex",
                gap: 0,
                borderRadius: 6,
                overflow: "hidden",
                border: "1px solid #dee2e6",
              }}
            >
              {[monedaLocal, "USD"].map((m, idx) => {
                const pend = pendientesPorMoneda[m] || 0;
                const activo = monedaVista === m;
                const badgeFirst = idx === 0;
                const badge = pend > 0 && (
                  <span
                    style={{
                      background: "#c62828",
                      color: "#fff",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      minWidth: 18,
                      height: 18,
                      borderRadius: 9,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "0 5px",
                    }}
                  >
                    {pend}
                  </span>
                );
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMonedaVista(m)}
                    title={
                      pend > 0
                        ? `${pend} movimiento${pend === 1 ? "" : "s"} pendiente${pend === 1 ? "" : "s"} de vincular`
                        : undefined
                    }
                    style={{
                      padding: "6px 22px",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      border: "none",
                      cursor: "pointer",
                      background: activo ? "#2c3e50" : "transparent",
                      color: activo ? "#fff" : "#666",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {badgeFirst ? badge : null}
                    {m}
                    {badgeFirst ? null : badge}
                  </button>
                );
              })}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                fontSize: "0.75rem",
                color: "#444",
                userSelect: "none",
              }}
            >
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", margin: 0 }}>
                <input
                  type="checkbox"
                  checked={consolidado}
                  onChange={(e) => setConsolidado(e.target.checked)}
                  style={{ cursor: "pointer" }}
                />
                Consolidado
              </label>
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: monedaVista === "USD" ? "not-allowed" : "pointer",
                  margin: 0,
                  opacity: monedaVista === "USD" ? 0.4 : 1,
                }}
                title={monedaVista === "USD" ? "Ya estás viendo en USD" : undefined}
              >
                <input
                  type="checkbox"
                  checked={monedaVista === "USD" ? false : convertirAUSD}
                  disabled={monedaVista === "USD"}
                  onChange={(e) => setConvertirAUSD(e.target.checked)}
                  style={{ cursor: monedaVista === "USD" ? "not-allowed" : "pointer" }}
                />
                Ver en US$
              </label>
            </div>
          </div>
        }
      >
        <div className={styles.headerStats}>
          <div className={styles.statItem}>
            <div className={styles.statLabel}>Saldo actual</div>
            <div className={styles.statValue}>{fmt(saldoActual)}</div>
          </div>
          <button
            type="button"
            onClick={handleSyncBanco}
            disabled={sincronizandoBanco || sincronizandoSII}
            style={{
              width: 150,
              padding: "6px 0",
              fontSize: "0.78rem",
              fontWeight: 600,
              border: "1px solid #2c3e50",
              borderRadius: 6,
              background: sincronizandoBanco ? "#95a5a6" : "#2c3e50",
              color: "#fff",
              cursor: sincronizandoBanco || sincronizandoSII ? "not-allowed" : "pointer",
              marginLeft: 12,
            }}
          >
            {sincronizandoBanco ? "Actualizando..." : "Actualizar banco"}
          </button>
          <button
            type="button"
            onClick={handleSyncSII}
            disabled={sincronizandoBanco || sincronizandoSII}
            style={{
              width: 150,
              padding: "6px 0",
              fontSize: "0.78rem",
              fontWeight: 600,
              border: "1px solid #6c3483",
              borderRadius: 6,
              background: sincronizandoSII ? "#95a5a6" : "#6c3483",
              color: "#fff",
              cursor: sincronizandoBanco || sincronizandoSII ? "not-allowed" : "pointer",
              marginLeft: 8,
            }}
          >
            {sincronizandoSII ? "Actualizando..." : `Actualizar ${enteFiscal}`}
          </button>
        </div>
      </Header>

      <div className={styles.content}>
        <div className={styles.grid} ref={gridRef}>
          <div className={styles.connector} />

          {/* Panel izquierdo */}
          <div className={styles.panelWrapper}>
            <div
              className={styles.panelHeader}
              style={{
                background: "#2c3e50",
                display: "flex",
                alignItems: "center",
                position: "relative",
              }}
            >
              <span>Movimientos históricos{ultimaConsulta ? " |" : ""}</span>
              {ultimaConsulta && (
                <span className={styles.ultimaSync}>Última carga: {format(ultimaConsulta, "dd/MM · HH:mm")}</span>
              )}
              <input
                type="text"
                placeholder="Buscar movimiento..."
                value={busquedaHistorico}
                onChange={(e) => setBusquedaHistorico(e.target.value)}
                className={styles.searchInputDark}
              />
              {pendientesVincular > 0 && (
                <span
                  style={{
                    background: "#c62828",
                    color: "#fff",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    minWidth: 20,
                    height: 20,
                    borderRadius: 10,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 5px",
                    position: "absolute",
                    right: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  {pendientesVincular}
                </span>
              )}
            </div>
            <div
              className={styles.filterBar}
              style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" }}
            >
              <div style={{ display: "flex", gap: 4 }}>
                {FILTROS_HISTORICO.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFiltroHistorico(f.key)}
                    className={`${styles.filterBtn} ${filtroHistorico === f.key ? styles.filterBtnActive : ""}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                {[
                  { key: "hoy", label: "Hoy" },
                  { key: "esta_semana", label: "Semana" },
                  { key: "este_mes", label: "Mes" },
                  { key: "ytd", label: "YTD" },
                ].map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFiltroTiempo(f.key)}
                    className={`${styles.filterBtn} ${filtroTiempo === f.key ? styles.filterBtnActive : ""}`}
                  >
                    {f.label}
                  </button>
                ))}
                <select
                  value={filtroTiempo.startsWith("mes_") ? filtroTiempo : ""}
                  onChange={(e) => e.target.value && setFiltroTiempo(e.target.value)}
                  style={{
                    padding: "5px 8px",
                    fontSize: "0.78rem",
                    border: "1px solid #dee2e6",
                    borderRadius: 4,
                    background: filtroTiempo.startsWith("mes_") ? "#2c3e50" : "#fff",
                    color: filtroTiempo.startsWith("mes_") ? "#fff" : "#333",
                    cursor: "pointer",
                  }}
                >
                  <option value="">Seleccionar…</option>
                  {mesesDropdown.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={styles.panel} style={{ borderRadius: "0 0 6px 6px" }}>
              {loading ? (
                <div className={styles.empty}>Cargando...</div>
              ) : movimientosFiltrados.length === 0 ? (
                <div className={styles.empty}>Sin movimientos</div>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Movimiento</th>
                      <th />
                      <th>Monto</th>
                      <th>Saldo</th>
                      <th>{consolidado ? "Ref" : "Edit"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movimientosFiltrados.map((m) => (
                      <tr
                        key={m.id}
                        className={isMovPendiente(m, pagosMovIds, movimientosBancarioFactoring) ? styles.pendiente : ""}
                      >
                        <td className={styles.fecha} style={bordeMonedaStyle(m.moneda_origen)}>
                          {formatFecha(m.fecha)}
                        </td>
                        <td className={styles.descripcion} title={m.descripcion_raw}>
                          {m.descripcion_raw}
                        </td>
                        <td className={styles.folio}>
                          {renderBadgeMovimiento(
                            m,
                            pagosMovIds,
                            enteFiscal,
                            movimientosBancarioFactoring,
                            mapaFactoring,
                            facturaInfoMap
                          )}
                        </td>
                        <td className={`${styles.right} ${m.tipo === "ingreso" ? styles.ingreso : styles.egreso}`}>
                          {fmt(m.monto_display ?? m.monto)}
                        </td>
                        <td className={styles.right}>{fmt(m.saldo_calculado)}</td>
                        <td className={styles.accion}>
                          {consolidado ? (
                            renderBadgeMoneda(m.moneda_origen)
                          ) : (
                            <FaEdit
                              title="Vincular"
                              style={{ cursor: "pointer", color: "#999", fontSize: "1em" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setVincularModal(m);
                              }}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Panel derecho */}
          <div className={styles.panelWrapper}>
            <div
              className={styles.panelHeader}
              style={{
                background: "#27ae60",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span>Movimientos futuros</span>
              <span aria-hidden="true" style={{ color: "#fff", opacity: 0.4 }}>
                |
              </span>
              <div style={{ display: "flex", gap: 4 }}>
                {calcularFiltrosTemporales().map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => {
                      if (filtroTemporalKey !== f.key) setFiltroTemporalKey(f.key);
                    }}
                    className={`${styles.bannerBtn} ${filtroTemporalKey === f.key ? styles.bannerBtnActive : ""}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <input
                type="text"
                placeholder="Buscar movimiento..."
                value={busquedaFuturos}
                onChange={(e) => setBusquedaFuturos(e.target.value)}
                className={styles.searchInputLight}
              />
              <span style={{ fontSize: "0.85rem", fontWeight: 700, opacity: 0.95, color: "#fff", marginLeft: "auto" }}>
                {fmt(saldoFinalProyectado)}
              </span>
            </div>
            <div className={styles.filterBar}>
              {FILTROS_PROYECCION.map((f) => {
                const enDesarrollo = f.key === "financiamiento";
                const btn = (
                  <button
                    type="button"
                    onClick={enDesarrollo ? undefined : () => toggleFiltro(f.key)}
                    disabled={enDesarrollo}
                    title={enDesarrollo ? "En desarrollo" : undefined}
                    className={`${styles.filterBtn} ${filtrosActivos.includes(f.key) ? styles.filterBtnActive : ""}`}
                    style={enDesarrollo ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                  >
                    {f.label}
                  </button>
                );
                if (f.key === "proveedores_facturados") {
                  return (
                    <span key={f.key} style={{ position: "relative", display: "inline-block" }}>
                      {btn}
                      {countPorPagarPendientes > 0 && (
                        <span
                          style={{
                            position: "absolute",
                            top: -6,
                            right: -6,
                            background: "#c62828",
                            color: "#fff",
                            fontSize: "0.55rem",
                            fontWeight: 700,
                            minWidth: 14,
                            height: 14,
                            borderRadius: 7,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0 3px",
                            pointerEvents: "none",
                            zIndex: 1,
                          }}
                        >
                          {countPorPagarPendientes}
                        </span>
                      )}
                    </span>
                  );
                }
                return <span key={f.key}>{btn}</span>;
              })}
            </div>
            <div className={styles.panel} style={{ borderRadius: "0 0 6px 6px" }}>
              {proyeccionesFuturas.length === 0 ? (
                <div className={styles.empty}>Sin proyecciones</div>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Concepto</th>
                      <th>Folio</th>
                      <th>Monto</th>
                      <th>Saldo</th>
                      <th>{consolidado ? "Ref" : "Edit"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proyeccionesFuturas.map((f, i) => (
                      <tr
                        key={`${f.id}-${i}`}
                        style={{ cursor: f.tipo === "facturado" ? "pointer" : "default" }}
                        onClick={() => {
                          if (f.tipo === "facturado") {
                            setPlanCuotasModal({ factura: f, tipo: "cobro", accionInicial: "fecha" });
                          }
                        }}
                      >
                        <td
                          className={`${styles.fecha} ${!f.fecha_display ? styles.sinFecha : ""}`}
                          style={{
                            ...(f.vencida ? { color: "#e74c3c", fontWeight: 600 } : {}),
                            ...(bordeMonedaStyle(f.moneda_origen) || {}),
                          }}
                        >
                          {f.fecha_display ? formatFecha(f.fecha_display) : "Sin fecha"}
                        </td>
                        <td className={styles.descripcion}>
                          {f.razon_social_receptor || "—"}
                          {f.estimado && (
                            <span
                              style={{
                                marginLeft: 6,
                                fontSize: "0.68rem",
                                background: "#f39c12",
                                color: "#fff",
                                borderRadius: 3,
                                padding: "1px 5px",
                              }}
                            >
                              Estimado
                            </span>
                          )}
                        </td>
                        <td className={styles.folio}>
                          {f.folio
                            ? f.cuota_actual
                              ? `${f.folio} (${f.cuota_actual}/${f.total_cuotas})`
                              : `${f.folio}`
                            : "—"}
                        </td>
                        <td
                          className={`${styles.right} ${
                            f.tipo === "pipeline"
                              ? styles.pipeline
                              : f.importeEnMoneda < 0
                                ? styles.egreso
                                : styles.ingreso
                          }`}
                        >
                          {fmt(Math.abs(f.importeEnMoneda))}
                        </td>
                        <td className={`${styles.right} ${f.saldo_proyectado >= 0 ? styles.ingreso : styles.egreso}`}>
                          {fmt(f.saldo_proyectado)}
                        </td>
                        <td className={styles.accion}>
                          {consolidado ? (
                            renderBadgeMoneda(f.moneda_origen)
                          ) : f.tipo === "facturado" || f.tipo === "proveedor" || f.tipo === "edificio" ? (
                            <FaEdit
                              title="Editar"
                              style={{ cursor: "pointer", color: "#999", fontSize: "1em" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (f.tipo === "facturado") {
                                  setPlanCuotasModal({ factura: f, tipo: "cobro", accionInicial: "cuotas" });
                                } else if (f.tipo === "proveedor") {
                                  // Factura SII real (id numérico) → AsignarRecibidaModal.
                                  // Entry sintética de recurrente (id "recurrente-..." string) → PlanCuotasModal.
                                  if (typeof f.id === "number") {
                                    setModalAsignarRecibida(f);
                                  } else {
                                    setPlanCuotasModal({
                                      factura: { ...f, id: f.id },
                                      tipo: "pago",
                                      accionInicial: f.cuota_actual ? "cuotas" : "fecha",
                                    });
                                  }
                                } else if (f.tipo === "edificio") {
                                  setPagoEdificioModal(f.edificioData);
                                }
                              }}
                            />
                          ) : (
                            <span style={{ color: "#ccc", fontSize: "0.85rem" }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {planCuotasModal && (
        <PlanCuotasModal
          isOpen={!!planCuotasModal}
          onClose={() => setPlanCuotasModal(null)}
          tipo={planCuotasModal.tipo}
          factura={planCuotasModal.factura}
          accionInicial={planCuotasModal.accionInicial || "fecha"}
          onSuccess={() => cargarDatosFuturos()}
        />
      )}

      {vincularModal && (
        <VincularMovimientoModal
          show={!!vincularModal}
          onClose={() => setVincularModal(null)}
          movimiento={vincularModal}
          onSuccess={handleVincularSuccess}
        />
      )}

      {pagoEdificioModal && (
        <PlanPagoEdificioModal
          isOpen={!!pagoEdificioModal}
          onClose={() => setPagoEdificioModal(null)}
          edificio={pagoEdificioModal}
          pais={pais}
          userId={userId}
          onSuccess={() => {
            setPagoEdificioModal(null);
            cargarDatosFuturos();
          }}
        />
      )}

      {modalAsignarRecibida && (
        <AsignarRecibidaModal
          factura={modalAsignarRecibida}
          isOpen={!!modalAsignarRecibida}
          toggle={() => setModalAsignarRecibida(null)}
          onAsignada={() => {
            setModalAsignarRecibida(null);
            cargarDatosFuturos();
          }}
        />
      )}
    </div>
  );
};

export default FlujoCajaV2;
